import { useEffect, useMemo, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import {
  sendAvailabilityRequest,
  sendBookingDetails,
  sendBookingHoursQuery,
  sendCalendarSummary,
  sendCalendarUpdated,
  sendNoWorkTomorrow
} from "@redux/actionCreators";
import {
  clearSendAvailabilityRequest,
  clearSendBookingDetails,
  clearSendBookingHoursQuery,
  clearSendCalendarSummary,
  clearSendCalendarUpdated,
  clearSendNoWorkTomorrow
} from "@redux/actionCreators/driver";

export const AVAILABILITY_REQUEST = "AVAILABILITY_REQUEST";
export const NO_WORK_TOMORROW = "NO_WORK_TOMORROW";
export const CALENDAR_SUMMARY = "CALENDAR_SUMMARY";
export const CALENDAR_UPDATED = "CALENDAR_UPDATED";
export const BOOKING_DETAILS = "BOOKING_DETAILS";
export const REQUEST_HRS = "REQUEST_HRS";

export const BOOKING_TEMPLATE_KEYS = [BOOKING_DETAILS, REQUEST_HRS];

const TEMPLATE_CATALOG = [
  {
    key: AVAILABILITY_REQUEST,
    label: "Availability request",
    requiresBooking: false,
    slice: "sendAvailabilityRequestInfo"
  },
  {
    key: NO_WORK_TOMORROW,
    label: "No work tomorrow",
    requiresBooking: false,
    slice: "sendNoWorkTomorrowInfo"
  },
  {
    key: CALENDAR_SUMMARY,
    label: "Calendar summary",
    requiresBooking: false,
    slice: "sendCalendarSummaryInfo"
  },
  {
    key: CALENDAR_UPDATED,
    label: "Calendar updated",
    requiresBooking: false,
    slice: "sendCalendarUpdatedInfo"
  },
  {
    key: BOOKING_DETAILS,
    label: "Booking details",
    requiresBooking: true,
    slice: "sendBookingDetailsInfo"
  },
  {
    key: REQUEST_HRS,
    label: "Request hrs",
    requiresBooking: true,
    slice: "sendBookingHoursQueryInfo"
  }
];

const previewText = slice => slice?.resultData?.Data?.MessageContent || "";

const useMessageTemplates = ({
  open,
  personObjectId,
  bookingObjectIds,
  hiddenTemplates = []
}) => {
  const dispatch = useDispatch();
  const driverReducer = useSelector(state => state?.driverReducer, shallowEqual);
  const previewCache = useRef({});
  const previewPersonId = useRef(personObjectId);
  if (previewPersonId.current !== personObjectId) {
    previewPersonId.current = personObjectId;
    previewCache.current = {};
  }

  const hiddenKey = hiddenTemplates.join(",");
  const bookingKey = (bookingObjectIds || []).join(",");
  const hasBooking = Array.isArray(bookingObjectIds) && bookingObjectIds.length > 0;

  const visibleCatalog = useMemo(() => {
    const hidden = new Set(hiddenTemplates);
    return TEMPLATE_CATALOG.filter(template => {
      if (hidden.has(template.key)) {
        return false;
      }
      if (template.requiresBooking) {
        return hasBooking;
      }
      return true;
    });
  }, [hiddenKey, hasBooking]);

  useEffect(() => {
    if (!open || !personObjectId) {
      return undefined;
    }

    const hidden = new Set(hiddenKey ? hiddenKey.split(",") : []);
    const personPreview = { personObjectId, template: true };

    if (!hidden.has(AVAILABILITY_REQUEST)) {
      dispatch(sendAvailabilityRequest(personPreview));
    }
    if (!hidden.has(NO_WORK_TOMORROW)) {
      dispatch(sendNoWorkTomorrow(personPreview));
    }
    if (!hidden.has(CALENDAR_UPDATED)) {
      dispatch(sendCalendarUpdated(personPreview));
    }
    if (!hidden.has(CALENDAR_SUMMARY)) {
      dispatch(
        sendCalendarSummary({
          PersonObjectId: personObjectId,
          Weeks: 1,
          GetTemplateOnly: true
        })
      );
    }

    if (hasBooking) {
      const bookingPreview = {
        PromptForConfirm: false,
        GetTemplateOnly: true,
        BookingObjectIds: bookingObjectIds
      };
      if (!hidden.has(BOOKING_DETAILS)) {
        dispatch(sendBookingDetails(bookingPreview));
      }
      if (!hidden.has(REQUEST_HRS)) {
        dispatch(
          sendBookingHoursQuery({
            GetTemplateOnly: true,
            BookingObjectIds: bookingObjectIds
          })
        );
      }
    }

    return () => {
      dispatch(clearSendAvailabilityRequest());
      dispatch(clearSendNoWorkTomorrow());
      dispatch(clearSendCalendarUpdated());
      dispatch(clearSendCalendarSummary());
      dispatch(clearSendBookingDetails());
      dispatch(clearSendBookingHoursQuery());
    };
  }, [open, personObjectId, hiddenKey, bookingKey, hasBooking, dispatch]);

  const templates = visibleCatalog.map(template => {
    const nextPreview = previewText(driverReducer?.[template.slice]);
    if (nextPreview) {
      previewCache.current[template.key] = nextPreview;
    }
    return {
      key: template.key,
      label: template.label,
      preview: nextPreview || previewCache.current[template.key] || ""
    };
  });

  const loading = visibleCatalog.some(template => driverReducer?.[template.slice]?.isLoading);

  return { templates, loading };
};

export default useMessageTemplates;
